/**
 * Rangos de fechas para los filtros de Reports: presets típicos + helpers de
 * conversión local YYYY-MM-DD. Todo en hora local del operador (los date
 * inputs nativos trabajan en local). `now` es inyectable para tests.
 */
export type PresetId =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | '90d'
  | 'this_month'
  | 'last_month'
  | 'ytd'
  | 'custom';

export interface DateRange {
  /** YYYY-MM-DD local (inclusive). */
  from: string;
  to: string;
}

export const PRESETS: ReadonlyArray<{ id: PresetId; label: string }> = [
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: '7d', label: 'Últimos 7 días' },
  { id: '30d', label: 'Últimos 30 días' },
  { id: '90d', label: 'Últimos 90 días' },
  { id: 'this_month', label: 'Este mes' },
  { id: 'last_month', label: 'Mes pasado' },
  { id: 'ytd', label: 'Año (YTD)' },
  { id: 'custom', label: 'Personalizado' },
];

export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

export function parseYmd(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(d.getTime()) ? d : null;
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Rango [from,to] de un preset. `custom` devuelve null (no fija fechas). */
export function presetRange(id: PresetId, now: Date = new Date()): DateRange | null {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = ymd(today);
  switch (id) {
    case 'today':
      return { from: to, to };
    case 'yesterday': {
      const y = ymd(addDays(today, -1));
      return { from: y, to: y };
    }
    case '7d':
      return { from: ymd(addDays(today, -6)), to };
    case '30d':
      return { from: ymd(addDays(today, -29)), to };
    case '90d':
      return { from: ymd(addDays(today, -89)), to };
    case 'this_month':
      return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to };
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: ymd(first), to: ymd(last) };
    }
    case 'ytd':
      return { from: ymd(new Date(now.getFullYear(), 0, 1)), to };
    case 'custom':
      return null;
  }
}

/** Detecta qué preset matchea un rango dado, o 'custom' si ninguno. */
export function detectPreset(range: DateRange, now: Date = new Date()): PresetId {
  for (const { id } of PRESETS) {
    if (id === 'custom') continue;
    const r = presetRange(id, now);
    if (r && r.from === range.from && r.to === range.to) return id;
  }
  return 'custom';
}

/** Date objects para los límites locales del rango (inicio y fin de día). */
export function rangeToInstants(range: DateRange): { from: Date; to: Date } | null {
  const f = parseYmd(range.from);
  const t = parseYmd(range.to);
  if (!f || !t) return null;
  return {
    from: new Date(f.getFullYear(), f.getMonth(), f.getDate(), 0, 0, 0, 0),
    to: new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999),
  };
}
